(function (root, factory) {
  'use strict';

  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (!root || !root.document) return;

  root.eSyncDeliveryEstimate = api;
  api.bootstrap(root);
})(typeof window !== 'undefined' ? window : globalThis, function () {
  'use strict';

  const PRODUCT_SELECTOR = '[data-esync-delivery-product]';
  const CART_SELECTOR = '[data-esync-delivery-cart]';
  const DATA_SELECTOR = '[data-esync-delivery-data]';

  const numberOrNull = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? Math.max(0, Math.trunc(numericValue)) : null;
  };

  const positiveQuantity = (value) => {
    const quantity = Number.parseInt(value, 10);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  };

  const displayTerm = (entry) => {
    if (!entry) return null;

    const min = numberOrNull(entry.min);
    const max = numberOrNull(entry.max);
    const label = typeof entry.term === 'string' ? entry.term.trim() : '';

    if (!label && min === null && max === null) return null;

    let computedLabel = label;
    if (!computedLabel && min !== null && max !== null) {
      computedLabel = min === max ? `${min} zile` : `${min}-${max} zile`;
    } else if (!computedLabel && min !== null) {
      computedLabel = `${min} zile`;
    } else if (!computedLabel && max !== null) {
      computedLabel = `${max} zile`;
    }

    return {
      label: computedLabel,
      min,
      max,
      name: typeof entry.name === 'string' ? entry.name : '',
    };
  };

  const fastestSupplier = (suppliers) => {
    const terms = (Array.isArray(suppliers) ? suppliers : [])
      .map(displayTerm)
      .filter(Boolean);
    const numericTerms = terms
      .filter((term) => term.min !== null && term.max !== null)
      .sort((left, right) => left.max - right.max || left.min - right.min);

    return numericTerms[0] || terms[0] || null;
  };

  const resolve = (variant, requestedQuantity) => {
    if (!variant) return null;
    if (variant.available === false) return null;

    const quantity = positiveQuantity(requestedQuantity);
    const stock = numberOrNull(variant.stock) || 0;
    const variantTerm = displayTerm(variant);
    const supplierTerm = fastestSupplier(variant.suppliers);

    if (stock > 0 && quantity <= stock && variantTerm) {
      return { ...variantTerm, source: 'stock', breakdown: '' };
    }

    if (quantity > stock && supplierTerm) {
      const overflow = quantity - stock;
      const breakdown = stock > 0 && variantTerm
        ? `${stock} buc. din stoc: ${variantTerm.label} · ${overflow} buc. aprovizionare externa: ${supplierTerm.label}`
        : '';

      return { ...supplierTerm, source: 'supplier', breakdown };
    }

    if (variantTerm) {
      return { ...variantTerm, source: 'fallback', breakdown: '' };
    }

    if (supplierTerm) {
      return { ...supplierTerm, source: 'supplier', breakdown: '' };
    }

    return null;
  };

  const largestEstimate = (estimates) => (Array.isArray(estimates) ? estimates : [])
    .filter(Boolean)
    .reduce((largest, estimate) => {
      if (!largest) return estimate;

      const largestMax = numberOrNull(largest.max) ?? numberOrNull(largest.min) ?? -1;
      const estimateMax = numberOrNull(estimate.max) ?? numberOrNull(estimate.min) ?? -1;
      if (estimateMax !== largestMax) return estimateMax > largestMax ? estimate : largest;

      const largestMin = numberOrNull(largest.min) ?? -1;
      const estimateMin = numberOrNull(estimate.min) ?? -1;
      return estimateMin > largestMin ? estimate : largest;
    }, null);

  const bootstrap = (windowObject) => {
    const { document } = windowObject;
    const state = {
      started: false,
      eventBus: null,
      productSubscriptions: new Map(),
      globalSubscriptions: [],
      cartAttributeTimer: null,
      cartAttributeSignature: null,
      cartAttributeRequest: null,
      pendingCartAttributes: null,
      observer: null,
      eventBusTimer: null,
    };

    const readData = (component) => {
      const dataNode = component.querySelector(DATA_SELECTOR);
      if (!dataNode) return null;

      try {
        return JSON.parse(dataNode.textContent);
      } catch (error) {
        console.warn('eSYNC: datele termenului de livrare nu sunt valide.', error);
        return null;
      }
    };

    const render = (component, result) => {
      const termNode = component.querySelector('[data-esync-delivery-term]');
      const breakdownNode = component.querySelector('[data-esync-delivery-breakdown]');
      const hiddenClass = component.matches(PRODUCT_SELECTOR)
        ? 'esync-delivery-estimate--hidden'
        : 'esync-delivery-line--hidden';

      component.classList.toggle(hiddenClass, !result);
      if (!result || !termNode) return;

      termNode.textContent = result.label;
      if (breakdownNode) {
        breakdownNode.textContent = result.breakdown || '';
        breakdownNode.hidden = !result.breakdown;
      }

      component.dispatchEvent(new windowObject.CustomEvent('esync:delivery:calculated', {
        bubbles: true,
        detail: result,
      }));
    };

    const productScope = (component) => component.closest('form[data-product-id]')
      || component.closest('.main-product')
      || component.closest('[data-product-id]')
      || document;

    const productQuantity = (component) => {
      const input = productScope(component).querySelector('[data-quantity-input], [name="quantity"]');
      return positiveQuantity(input?.value || 1);
    };

    const productVariantId = (component) => {
      if (component.dataset.currentVariantId) return component.dataset.currentVariantId;
      const input = productScope(component).querySelector('[name="id"]');
      return String(input?.value || component.dataset.initialVariantId || '');
    };

    const updateProduct = (component, variantId = null, quantity = null, variantAvailability = null) => {
      const data = readData(component);
      if (!data?.variants) return null;

      const selectedVariantId = String(variantId || productVariantId(component));
      component.dataset.currentVariantId = selectedVariantId;
      const selectedVariant = data.variants[selectedVariantId];
      const variant = typeof variantAvailability === 'boolean'
        ? { ...selectedVariant, available: variantAvailability }
        : selectedVariant;
      const result = resolve(variant, quantity ?? productQuantity(component));
      render(component, result);
      return result;
    };

    const cartQuantity = (component, data) => {
      const item = component.closest('[data-cart-item], .scd-item, .sf-cart__table-row');
      const inputs = item
        ? item.querySelectorAll('.scd-item__qty_input, [name="updates[]"]')
        : [];

      for (const input of inputs) {
        if (input.offsetParent !== null || inputs.length === 1) {
          return positiveQuantity(input.value);
        }
      }

      return positiveQuantity(data.quantity || 1);
    };

    const updateCartComponent = (component) => {
      const data = readData(component);
      if (!data?.variant) return null;

      const result = resolve(data.variant, cartQuantity(component, data));
      render(component, result);
      return result;
    };

    const visibleCartComponents = () => {
      const components = Array.from(document.querySelectorAll(CART_SELECTOR));
      const visible = components.filter((component) => component.offsetParent !== null);
      return visible.length ? visible : components;
    };

    const currentCartEstimate = () => largestEstimate(
      visibleCartComponents().map((component) => {
        const data = readData(component);
        return data?.variant ? resolve(data.variant, cartQuantity(component, data)) : null;
      }),
    );

    const sendCartAttributes = (attributes) => {
      const signature = JSON.stringify(attributes);
      if (signature === state.cartAttributeSignature) {
        return state.cartAttributeRequest || Promise.resolve();
      }

      if (state.cartAttributeRequest) {
        state.pendingCartAttributes = attributes;
        return state.cartAttributeRequest;
      }

      const shopRoot = windowObject.Shopify?.routes?.root || '/';
      state.cartAttributeRequest = windowObject.fetch(`${shopRoot}cart/update.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attributes }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Shopify cart/update.js: ${response.status}`);
          state.cartAttributeSignature = signature;
          return response.json();
        })
        .then((cart) => {
          document.dispatchEvent(new windowObject.CustomEvent('esync:cart-attributes:saved', {
            detail: { attributes, cart },
          }));
          return cart;
        })
        .catch((error) => {
          console.warn('eSYNC: termenul de livrare nu a putut fi salvat in cos.', error);
        })
        .finally(() => {
          state.cartAttributeRequest = null;
          const pendingAttributes = state.pendingCartAttributes;
          state.pendingCartAttributes = null;
          if (pendingAttributes && JSON.stringify(pendingAttributes) !== state.cartAttributeSignature) {
            sendCartAttributes(pendingAttributes);
          }
        });

      return state.cartAttributeRequest;
    };

    const persistCartEstimate = () => {
      state.cartAttributeTimer = null;
      if (!document.querySelector(CART_SELECTOR)) return;

      const estimate = currentCartEstimate();
      sendCartAttributes({
        _esync_delivery_term: estimate?.label || '',
        _esync_delivery_min: estimate?.min === null || estimate?.min === undefined ? '' : String(estimate.min),
        _esync_delivery_max: estimate?.max === null || estimate?.max === undefined ? '' : String(estimate.max),
      });
    };

    const queueCartEstimate = () => {
      if (state.cartAttributeTimer) windowObject.clearTimeout(state.cartAttributeTimer);
      state.cartAttributeTimer = windowObject.setTimeout(persistCartEstimate, 120);
    };

    const componentsForProduct = (productId) => document.querySelectorAll(
      `${PRODUCT_SELECTOR}[data-product-id="${productId}"]`,
    );

    const subscribeProduct = (component) => {
      const productId = component.dataset.productId;
      if (!productId || !state.eventBus || state.productSubscriptions.has(productId)) return;

      const unsubscribeVariant = state.eventBus.subscribe(`${productId}__VARIANT_CHANGE`, (variant) => {
        componentsForProduct(productId).forEach((node) => updateProduct(node, variant?.id, null, variant?.available));
      });
      const unsubscribeQuantity = state.eventBus.subscribe(`${productId}__QUANTITY_CHANGE`, (quantity) => {
        componentsForProduct(productId).forEach((node) => updateProduct(node, null, quantity));
      });

      state.productSubscriptions.set(productId, [unsubscribeVariant, unsubscribeQuantity]);
    };

    const hydrate = (scope = document) => {
      const productComponents = [];
      const cartComponents = [];

      if (scope instanceof windowObject.Element) {
        if (scope.matches(PRODUCT_SELECTOR)) productComponents.push(scope);
        if (scope.matches(CART_SELECTOR)) cartComponents.push(scope);
      }

      scope.querySelectorAll?.(PRODUCT_SELECTOR).forEach((component) => productComponents.push(component));
      scope.querySelectorAll?.(CART_SELECTOR).forEach((component) => cartComponents.push(component));

      productComponents.forEach((component) => {
        subscribeProduct(component);
        updateProduct(component);
      });
      cartComponents.forEach(updateCartComponent);
      if (cartComponents.length) queueCartEstimate();
    };

    const connectThemeEvents = () => {
      const eventBus = windowObject._ThemeEvent;
      if (!eventBus || typeof eventBus.subscribe !== 'function') return false;
      if (state.eventBus === eventBus) return true;

      state.eventBus = eventBus;
      document.querySelectorAll(PRODUCT_SELECTOR).forEach(subscribeProduct);
      state.globalSubscriptions.push(eventBus.subscribe('ON_ITEM_ADDED', () => {
        windowObject.requestAnimationFrame(() => hydrate());
      }));
      state.globalSubscriptions.push(eventBus.subscribe('ON_CART_UPDATE', () => {
        windowObject.requestAnimationFrame(() => hydrate());
      }));
      return true;
    };

    const waitForThemeEvents = () => {
      if (connectThemeEvents()) return;
      if (state.eventBusTimer) return;

      state.eventBusTimer = windowObject.setInterval(() => {
        if (connectThemeEvents()) {
          windowObject.clearInterval(state.eventBusTimer);
          state.eventBusTimer = null;
        }
      }, 50);
    };

    const delayedComponentRefresh = (target) => {
      windowObject.setTimeout(() => {
        const component = target?.closest?.(`${PRODUCT_SELECTOR}, ${CART_SELECTOR}`);
        if (component?.matches(PRODUCT_SELECTOR)) updateProduct(component);
        else if (component?.matches(CART_SELECTOR)) {
          updateCartComponent(component);
          queueCartEstimate();
        }
      }, 0);
    };

    const initialize = () => {
      if (state.started) {
        hydrate();
        waitForThemeEvents();
        return;
      }
      state.started = true;

      hydrate();
      waitForThemeEvents();

      document.addEventListener('input', (event) => {
        if (event.target.matches('[data-quantity-input], [name="quantity"], .scd-item__qty_input, [name="updates[]"]')) {
          delayedComponentRefresh(event.target);
        }
      });

      document.addEventListener('change', (event) => {
        if (event.target.matches('[name="id"], [data-quantity-input], [name="quantity"], .scd-item__qty_input, [name="updates[]"]')) {
          delayedComponentRefresh(event.target);
        }
      });

      document.addEventListener('click', (event) => {
        const openButton = event.target.closest('[data-esync-delivery-open]');
        if (openButton) {
          const dialog = openButton.closest(PRODUCT_SELECTOR)?.querySelector('[data-esync-delivery-dialog]');
          if (dialog?.showModal) dialog.showModal();
          else dialog?.setAttribute('open', 'open');
          return;
        }

        const closeButton = event.target.closest('[data-esync-delivery-close]');
        if (closeButton) {
          const dialog = closeButton.closest('[data-esync-delivery-dialog]');
          if (dialog?.close) dialog.close();
          else dialog?.removeAttribute('open');
          return;
        }

        if (event.target.matches('[data-esync-delivery-dialog]')) {
          const rectangle = event.target.getBoundingClientRect();
          const outside = event.clientX < rectangle.left
            || event.clientX > rectangle.right
            || event.clientY < rectangle.top
            || event.clientY > rectangle.bottom;
          if (outside) event.target.close?.();
          return;
        }

        if (event.target.closest('[data-quantity-selector], [data-qty-change]')) {
          delayedComponentRefresh(event.target);
        }
      });

      document.addEventListener('shopify:section:load', (event) => hydrate(event.target));
      document.addEventListener('esync:delivery:refresh', () => hydrate());

      state.observer = new windowObject.MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (!(node instanceof windowObject.Element)) return;
            if (node.matches(PRODUCT_SELECTOR) || node.matches(CART_SELECTOR)) hydrate(node);
            else if (node.querySelector(`${PRODUCT_SELECTOR}, ${CART_SELECTOR}`)) hydrate(node);
          });
        });
      });
      state.observer.observe(document.body, { childList: true, subtree: true });
    };

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initialize, { once: true });
    } else {
      initialize();
    }

    api.refresh = hydrate;
  };

  return {
    numberOrNull,
    displayTerm,
    fastestSupplier,
    resolve,
    largestEstimate,
    bootstrap,
    refresh: function () {},
  };
});
